<template>
  <highlight-card
    :button="button"
    :heading="`${common.inMemoryOf} ${deceasedName}`"
    :status="raised"
    :text="text"
    @action="give"
  >
    <template #media>
      <div
        class="memorial-gift-card__scene"
        role="img"
        :aria-label="sprintf(texts.certificateAlt, certificateName)"
      >
        <certificate-scene
          :heading="common.inMemoryOf"
          :name="certificateName"
          :thanks="thanks"
          :zoom="1"
        />
      </div>
    </template>
  </highlight-card>
</template>

<script lang="ts" setup>
  import { sprintf } from "sprintf-js";
  import { computed } from "vue";
  import { useStore } from "vuex";
  import { quickContent } from "../../../composables/useSlideshow";
  import { useTotalDonations } from "../../../composables/useTotalDonations";
  import { DONATION_INITIAL } from "../../../createCommentStepsHelper";
  import { commonTexts, highlightTexts } from "../../../highlights/texts";
  import CertificateScene from "../../MemorialGift/CertificateScene.vue";
  import type { CardButton } from "../HighlightCard.vue";
  import HighlightCard from "../HighlightCard.vue";

  interface MemorialFund {
    name?: string;
    pennybridge_id?: string | null;
    url?: string;
  }

  // "Donate here." ends a sentence in the old box; a button has no full stop.
  const TRAILING_STOP = /\.$/;

  declare const theme_vars: { currentSite: string };

  /** Totals come from the donations store, which the block loads. */
  const props = defineProps<{
    deceasedName: string;
    memorialFund: MemorialFund | null;
  }>();

  const store = useStore();
  const texts = highlightTexts();
  const common = commonTexts();
  const { totalDonations, hasSingleDonations } = useTotalDonations();
  const dk = theme_vars.currentSite === "DK";

  // The certificate names them as the printed one does: without the nickname.
  const deceased = quickContent()?.deceased;
  const certificateName =
    deceased?.formalName || deceased?.name || props.deceasedName;

  const thanks = computed(() =>
    props.memorialFund?.name
      ? sprintf(texts.certificateFundThanks, props.memorialFund.name)
      : texts.certificateThanks
  );

  // A fund without a PennyBridge id takes gifts on its own site.
  const external = computed(
    () => !!props.memorialFund?.url && !props.memorialFund.pennybridge_id
  );

  const text = computed(() => {
    const fund = props.memorialFund;
    if (fund?.name) {
      return sprintf(
        texts.totalFundsWithFundDescription,
        props.deceasedName,
        fund.name
      );
    }
    const start = dk
      ? texts.totalFundsWithoutFundDescriptionDK
      : texts.totalFundsWithoutFundDescription;
    const end = dk
      ? texts.totalFundsWithoutFundCtaDK
      : texts.totalFundsWithoutFundCta;
    return `${sprintf(start, props.deceasedName)} ${end}`;
  });

  const collected = computed(() =>
    hasSingleDonations.value
      ? sprintf(texts.totalFundsCountText, totalDonations.value.amount)
      : ""
  );

  const giverCount = computed(() => Number(totalDonations.value?.count) || 0);

  // The certificate takes the fact box's place, so the status line carries the givers too.
  const raised = computed(() => {
    if (!collected.value) {
      return "";
    }
    const { amount } = totalDonations.value;
    if (giverCount.value > 1) {
      return sprintf(texts.raisedByMany, amount, giverCount.value);
    }
    return giverCount.value === 1
      ? sprintf(texts.raisedByOne, amount)
      : collected.value;
  });

  const button = computed<CardButton>(() =>
    external.value
      ? {
          external: true,
          href: props.memorialFund?.url,
          label: texts.totalFundsCtaExternal.replace(TRAILING_STOP, ""),
        }
      : { label: texts.giftCta }
  );

  function give() {
    store.dispatch("createComment/toggleModal", DONATION_INITIAL);
  }
</script>
